import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  Chip,
  Divider,
  Stack,
  Paper,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import Close from '@mui/icons-material/Close'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import AccessTime from '@mui/icons-material/AccessTime'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import EventAvailable from '@mui/icons-material/EventAvailable'
import EventBusy from '@mui/icons-material/EventBusy'
import Event from '@mui/icons-material/Event'
import LocalOffer from '@mui/icons-material/LocalOffer'
import Person from '@mui/icons-material/Person'
import Tv from '@mui/icons-material/Tv'
import Lock from '@mui/icons-material/Lock'
import LockOpen from '@mui/icons-material/LockOpen'
import Info from '@mui/icons-material/Info'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Refresh from '@mui/icons-material/Refresh'
import Visibility from '@mui/icons-material/Visibility'
import Schedule from '@mui/icons-material/Schedule'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 600,
    maxWidth: '95vw',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
  },
}))

const DrawerHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
}))

const DayCard = styled(Paper)(({ theme, isAvailable }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: `2px solid ${isAvailable ? theme.palette.success.light : theme.palette.grey[200]}`,
  backgroundColor: isAvailable
    ? alpha(theme.palette.success.main, 0.04)
    : theme.palette.background.paper,
  transition: 'all 0.2s ease',
}))

const TimeSlotChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '0.8rem',
  borderRadius: theme.spacing(1),
  '& .MuiChip-icon': {
    color: theme.palette.primary.main,
  },
}))

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
}))

const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']

const formatDayName = (day) => {
  return day?.charAt(0) + day?.slice(1).toLowerCase()
}

const formatTime = (time) => {
  if (!time) return ''
  // Convert 24hr format to 12hr format
  const [hours, minutes] = time.split(':')
  const hour = parseInt(hours, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'N/A'
  try {
    const date = new Date(dateTimeStr)
    if (Number.isNaN(date.getTime())) return 'N/A'
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return dateTimeStr
  }
}

const isExpired = (expiresOn) => {
  if (!expiresOn) return false
  try {
    return new Date(expiresOn) < new Date()
  } catch {
    return false
  }
}

const formatExpiration = (expiresOn) => {
  if (!expiresOn) return 'No expiration'
  const expired = isExpired(expiresOn)
  const formatted = formatDateTime(expiresOn)
  return expired ? `Expired: ${formatted}` : `Expires: ${formatted}`
}

export default function ChamberRoomRosterDrawer({
  open,
  onClose,
  chamberRoom,
  clinicLocationTitle,
}) {
  const [refreshedRoom, setRefreshedRoom] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Refresh occupancy status when drawer opens
  const refreshOccupancyStatus = useCallback(async () => {
    if (!chamberRoom?.id || !chamberRoom?.clinicBrandId || !chamberRoom?.clinicLocationId) return

    setIsRefreshing(true)
    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/csm/chamber-rooms/status',
        {
          clinicBrandId: chamberRoom.clinicBrandId,
          clinicLocationId: chamberRoom.clinicLocationId,
          chamberRoomId: chamberRoom.id,
        },
      )

      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const statusList = getDataMapFromCommonResponse(resp)?.['chamberRoomStatus'] || []
        const status = statusList.find((s) => s.chamberRoomId === chamberRoom.id)
        if (status) {
          setRefreshedRoom({
            ...chamberRoom,
            mentorPresent: status.mentorPresent,
            occupied: status.occupied,
            checkedInMentorEffectiveId: status.checkedInMentorEffectiveId,
            checkedInMentorEffectiveName: status.checkedInMentorEffectiveName,
            checkedInMentorId: status.checkedInMentorId,
            checkedInMentorName: status.checkedInMentorName,
            checkedInMethod: status.checkedInMethod,
            mentorLastCheckedIn: status.mentorLastCheckedIn,
            mentorLastCheckinRefreshed: status.mentorLastCheckinRefreshed,
            mentorLastCheckedOut: status.mentorLastCheckedOut,
          })
        }
      }
    } catch (err) {
      console.error('Error refreshing occupancy status:', err)
    } finally {
      setIsRefreshing(false)
    }
  }, [chamberRoom])

  useEffect(() => {
    if (open && chamberRoom) {
      refreshOccupancyStatus()
      setRefreshedRoom(chamberRoom)
    }
  }, [open, chamberRoom, refreshOccupancyStatus])

  if (!chamberRoom) return null

  const displayRoom = refreshedRoom || chamberRoom
  const rosterSchedule = displayRoom?.rosterSchedule
  const weeklyHourMap = rosterSchedule?.weeklyHourMap || {}
  const isRosterEnabled = rosterSchedule?.enabled === true
  const hasRosterSchedule = !!rosterSchedule
  const rosterEventTypes = rosterSchedule?.eventTypes || []
  const targetSpecialitySlugs = rosterSchedule?.targetSpecialitySlugs || []
  const description = displayRoom?.description
  const accessScope = displayRoom?.accessAllowedTo
  const hasAUMHUMScreen = displayRoom?.hasAUMHUMScreenInstalled === true
  const mentorPresent = displayRoom?.mentorPresent === true
  const isOccupied = displayRoom?.occupied === true
  const checkedInMentorEffectiveName = displayRoom?.checkedInMentorEffectiveName
  const checkedInMentorEffectiveId = displayRoom?.checkedInMentorEffectiveId
  const checkedInMethod = displayRoom?.checkedInMethod
  const mentorLastCheckedIn = displayRoom?.mentorLastCheckedIn
  const mentorLastCheckinRefreshed = displayRoom?.mentorLastCheckinRefreshed
  const mentorLastCheckedOut = displayRoom?.mentorLastCheckedOut
  const roomEventTypes = displayRoom?.eventTypesForThisRoom || []
  const associatedMentors = displayRoom?.associatedMentors || []
  const mentorsAndAuditors = displayRoom?.mentorsAndAuditors || []
  const auditorAccess = displayRoom?.auditorAccess

  const sortedDays = DAY_ORDER.filter((day) => weeklyHourMap[day])

  return (
    <StyledDrawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <DrawerHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h5" fontWeight={600}>
              Chamber Room Details
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Refresh occupancy status">
                <IconButton
                  onClick={refreshOccupancyStatus}
                  size="small"
                  disabled={isRefreshing}
                  sx={{ color: 'white' }}
                >
                  <Refresh 
                    fontSize="small" 
                    sx={{
                      animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                <Close />
              </IconButton>
            </Box>
          </Box>
          <InfoRow sx={{ mb: 0, opacity: 0.9 }}>
            <MeetingRoom sx={{ fontSize: 18 }} />
            <Typography variant="body2">{displayRoom?.title || 'Chamber Room'}</Typography>
          </InfoRow>
          {clinicLocationTitle && (
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {clinicLocationTitle}
            </Typography>
          )}
        </DrawerHeader>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {/* Occupancy Status Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              backgroundColor: mentorPresent
                ? alpha('#f59e0b', 0.08)
                : alpha('#10b981', 0.08),
              border: `2px solid ${
                mentorPresent ? alpha('#f59e0b', 0.3) : alpha('#10b981', 0.3)
              }`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Person color={mentorPresent ? 'warning' : 'success'} />
              <Typography variant="subtitle1" fontWeight={600}>
                Occupancy Status
              </Typography>
              <Chip
                size="small"
                label={mentorPresent ? 'Occupied' : 'Available'}
                color={mentorPresent ? 'warning' : 'success'}
                sx={{ ml: 1 }}
              />
            </Box>
            <Stack spacing={1.5}>
              {mentorPresent && checkedInMentorEffectiveName ? (
                <>
                  <InfoRow>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                      Occupied by:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {checkedInMentorEffectiveName}
                    </Typography>
                  </InfoRow>
                  {checkedInMentorEffectiveId && (
                    <InfoRow>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                        Mentor ID:
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {checkedInMentorEffectiveId}
                      </Typography>
                    </InfoRow>
                  )}
                  {checkedInMethod && (
                    <InfoRow>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                        Check-in Method:
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          checkedInMethod === 'check-in-method-in-person' ? 'In-Person' : 'Remote'
                        }
                        color="info"
                        variant="outlined"
                      />
                    </InfoRow>
                  )}
                  {mentorLastCheckedIn && (
                    <InfoRow>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                        Checked in:
                      </Typography>
                      <Typography variant="body2">{formatDateTime(mentorLastCheckedIn)}</Typography>
                    </InfoRow>
                  )}
                  {mentorLastCheckinRefreshed && (
                    <InfoRow>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                        Last refresh:
                      </Typography>
                      <Typography variant="body2">{formatDateTime(mentorLastCheckinRefreshed)}</Typography>
                    </InfoRow>
                  )}
                </>
              ) : (
                <InfoRow>
                  <Typography variant="body2" color="text.secondary">
                    Room is currently available and not occupied.
                  </Typography>
                </InfoRow>
              )}
              {mentorLastCheckedOut && !mentorPresent && (
                <InfoRow>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                    Last checked out:
                  </Typography>
                  <Typography variant="body2">{formatDateTime(mentorLastCheckedOut)}</Typography>
                </InfoRow>
              )}
            </Stack>
          </Paper>

          {/* Mentors and Auditors Access Section */}
          {mentorsAndAuditors.length > 0 && (
            <Accordion defaultExpanded sx={{ mb: 2, boxShadow: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Person color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Mentors & Auditors Access
                  </Typography>
                  <Chip label={mentorsAndAuditors.length} size="small" color="primary" sx={{ ml: 1 }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {mentorsAndAuditors.map((entry, idx) => {
                    const mentorId = entry.mentorId
                    const checkinMethods = entry.checkinMethods || []
                    const auditorIds = entry.auditorIds || []
                    const auditAccessList = entry.auditAccessList || []
                    const isEnabled = entry.enabled !== false

                    // Find mentor name from associated mentors
                    const mentorInfo = associatedMentors.find((m) => m.id === mentorId)

                    return (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          backgroundColor: isEnabled ? 'background.paper' : 'grey.50',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                              {mentorInfo?.name || mentorId}
                            </Typography>
                            {mentorInfo?.id && (
                              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                ({mentorInfo.id})
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            label={isEnabled ? 'Enabled' : 'Disabled'}
                            color={isEnabled ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </Box>

                        {checkinMethods.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                              Check-in Methods:
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {checkinMethods.map((method, mIdx) => (
                                <Chip
                                  key={mIdx}
                                  size="small"
                                  label={method === 'check-in-method-in-person' ? 'In-Person' : 'Remote'}
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {auditorIds.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                              Static Auditors ({auditorIds.length}):
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {auditorIds.map((auditorId, aIdx) => (
                                <Chip
                                  key={aIdx}
                                  size="small"
                                  icon={<Visibility />}
                                  label={auditorId}
                                  variant="outlined"
                                  color="secondary"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {auditAccessList.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                              Time-bound Auditor Access:
                            </Typography>
                            <List dense sx={{ py: 0 }}>
                              {auditAccessList.map((access, aIdx) => {
                                const expired = isExpired(access.expiresOn)
                                return (
                                  <ListItem
                                    key={aIdx}
                                    sx={{
                                      py: 0.5,
                                      px: 1,
                                      borderRadius: 1,
                                      backgroundColor: expired ? alpha('#ef4444', 0.05) : alpha('#10b981', 0.05),
                                      mb: 0.5,
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <Schedule fontSize="small" color={expired ? 'error' : 'success'} />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="body2" fontFamily="monospace">
                                            {access.auditorMentorId}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            label={expired ? 'Expired' : 'Active'}
                                            color={expired ? 'error' : 'success'}
                                            sx={{ height: 18, fontSize: '0.65rem' }}
                                          />
                                        </Box>
                                      }
                                      secondary={
                                        <Typography variant="caption" color="text.secondary">
                                          {formatExpiration(access.expiresOn)}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                )
                              })}
                            </List>
                          </Box>
                        )}

                        {auditorIds.length === 0 && auditAccessList.length === 0 && (
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            No auditors assigned
                          </Typography>
                        )}
                      </Paper>
                    )
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Room Information Section */}
          <Accordion defaultExpanded sx={{ mb: 2, boxShadow: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Info color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Room Information
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <InfoRow>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                    Room ID:
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                    {displayRoom?.id}
                  </Typography>
                </InfoRow>
                {description && (
                  <InfoRow>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                      Description:
                    </Typography>
                    <Typography variant="body2">{description}</Typography>
                  </InfoRow>
                )}
                <InfoRow>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                    Status:
                  </Typography>
                  <Chip
                    size="small"
                    icon={displayRoom?.enabled ? <CheckCircle /> : <Cancel />}
                    label={displayRoom?.enabled ? 'Enabled' : 'Disabled'}
                    color={displayRoom?.enabled ? 'success' : 'error'}
                    variant="outlined"
                  />
                </InfoRow>
                <InfoRow>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                    Rostered:
                  </Typography>
                  <Chip
                    size="small"
                    icon={displayRoom?.rostered ? <EventAvailable /> : <EventBusy />}
                    label={displayRoom?.rostered ? 'Yes' : 'No'}
                    color={displayRoom?.rostered ? 'info' : 'default'}
                    variant="outlined"
                  />
                </InfoRow>
                {hasAUMHUMScreen && (
                  <InfoRow>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                      Screen:
                    </Typography>
                    <Chip
                      size="small"
                      icon={<Tv />}
                      label="AUMHUM Screen Installed"
                      color="info"
                      variant="outlined"
                    />
                  </InfoRow>
                )}
                {accessScope && (
                  <InfoRow>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                      Access Scope:
                    </Typography>
                    <Chip
                      size="small"
                      icon={accessScope === 'Explicitly-Allowed-List' ? <Lock /> : <LockOpen />}
                      label={accessScope.replace(/-/g, ' ')}
                      variant="outlined"
                    />
                  </InfoRow>
                )}
                {roomEventTypes.length > 0 && (
                  <InfoRow>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                      Event Types:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {roomEventTypes.map((eventType, idx) => (
                        <Chip
                          key={idx}
                          size="small"
                          icon={<Event />}
                          label={eventType}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </InfoRow>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Roster Schedule Section */}
          {hasRosterSchedule && (
            <Accordion defaultExpanded sx={{ mb: 2, boxShadow: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarMonth color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Weekly Schedule
                  </Typography>
                  <Chip
                    size="small"
                    label={isRosterEnabled ? 'Active' : 'Inactive'}
                    color={isRosterEnabled ? 'success' : 'default'}
                    sx={{ ml: 1 }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {sortedDays.length > 0 ? (
                  <Stack spacing={2}>
                    {sortedDays.map((day) => {
                      const daySchedule = weeklyHourMap[day]
                      const isAvailable = daySchedule?.available === true
                      const slots = daySchedule?.slotList || []

                      return (
                        <DayCard key={day} isAvailable={isAvailable} elevation={0}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: slots.length > 0 ? 1.5 : 0,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isAvailable
                                    ? alpha('#10b981', 0.15)
                                    : alpha('#6b7280', 0.1),
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{
                                    color: isAvailable ? '#10b981' : '#6b7280',
                                  }}
                                >
                                  {day?.substring(0, 3)}
                                </Typography>
                              </Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {formatDayName(day)}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={isAvailable ? 'Available' : 'Unavailable'}
                              color={isAvailable ? 'success' : 'default'}
                              variant={isAvailable ? 'filled' : 'outlined'}
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>

                          {isAvailable && slots.length > 0 && (
                            <>
                              <Divider sx={{ my: 1.5 }} />
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {slots.map((slot, index) => (
                                  <TimeSlotChip
                                    key={index}
                                    size="small"
                                    icon={<AccessTime sx={{ fontSize: 14 }} />}
                                    label={`${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`}
                                  />
                                ))}
                              </Stack>
                            </>
                          )}
                        </DayCard>
                      )
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <CalendarMonth sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.4, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No weekly schedule configured
                    </Typography>
                  </Box>
                )}

                {/* Schedule Configuration */}
                {(rosterSchedule?.id || rosterEventTypes.length > 0 || targetSpecialitySlugs.length > 0) && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Schedule Configuration
                    </Typography>
                    <Stack spacing={1.5}>
                      {rosterSchedule?.id && (
                        <InfoRow>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                            Schedule ID:
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                            {rosterSchedule.id}
                          </Typography>
                        </InfoRow>
                      )}
                      {rosterEventTypes.length > 0 && (
                        <InfoRow>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                            Allowed Event Types:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {rosterEventTypes.map((eventType, idx) => (
                              <Chip
                                key={idx}
                                size="small"
                                icon={<Event />}
                                label={eventType}
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </InfoRow>
                      )}
                      {targetSpecialitySlugs.length > 0 && (
                        <InfoRow>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                            Target Specialities:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {targetSpecialitySlugs.map((speciality, idx) => (
                              <Chip
                                key={idx}
                                size="small"
                                icon={<LocalOffer />}
                                label={speciality}
                                color="secondary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </InfoRow>
                      )}
                    </Stack>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {!hasRosterSchedule && (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <EventBusy sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.4, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Roster Schedule Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This chamber room does not have an accessible roster schedule.
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </StyledDrawer>
  )
}
